import React, { useState, useEffect } from 'react';
import { X, Users, Play, Edit3, Plus, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './ScriptDetailModal.css';

function ScriptDetailModal({ script, isOpen, onClose, onViewScript, onAddCreator, onDeleteScript, onApproveScript, onRejectScript, campaignBriefs, campaignId, onOpenApprovedModal }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [recentlyCompleted, setRecentlyCompleted] = useState([]);
  const [loadingCompleted, setLoadingCompleted] = useState(false);

  // Fetch recently completed videos from briefs containing this script
  useEffect(() => {
    const fetchRecentlyCompleted = async () => {
      if (!isOpen || !script || !campaignId || !campaignBriefs || !onOpenApprovedModal) {
        setRecentlyCompleted([]);
        return;
      }

      setLoadingCompleted(true);
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setRecentlyCompleted([]);
          setLoadingCompleted(false);
          return;
        }

        const scriptName = script.name || script.title || script.scriptName || script.scriptTitle;
        const scriptId = String(script.id || script.scriptId || '');

        // Get all briefs for this campaign that contain this script
        const campaignBriefsForScript = campaignBriefs.filter(b => {
          if (b.campaignId !== campaignId) return false;
          
          // Check if this brief contains the script
          const briefScripts = b.scripts || [];
          const hasScript = briefScripts.some(s => {
            const sId = String(s.id || s.scriptId || '');
            const sName = (s.name || s.title || s.scriptName || s.scriptTitle || '').trim();
            return (scriptId && sId === scriptId) || 
                   (scriptName && sName && sName.toLowerCase() === scriptName.trim().toLowerCase());
          });
          
          return hasScript;
        });

        const completedList = [];
        const processedProjectIds = new Set();

        // Fetch submissions from all briefs that contain this script
        for (const brief of campaignBriefsForScript) {
          try {
            const submissionsRef = collection(
              db,
              'users',
              user.uid,
              'campaigns',
              campaignId,
              'briefs',
              brief.id,
              'submissions'
            );

            const submissionsSnapshot = await getDocs(submissionsRef);

            for (const submissionDoc of submissionsSnapshot.docs) {
              const submissionData = submissionDoc.data();
              const submissionStatus = submissionData.status;

              // Only process approved submissions
              if (submissionStatus !== 'approved' && submissionStatus !== 'partially_approved') {
                continue;
              }

              const projectId = submissionData.projectId;
              if (!projectId || processedProjectIds.has(projectId)) {
                continue;
              }

              // Get approved videos from submission
              const approvedVideos = submissionData.content || [];
              const completedVideos = approvedVideos.filter(video => {
                const videoType = video.type || video.variationType;
                const isMainVideo = !videoType || videoType === 'video' || (videoType !== 'hook' && videoType !== 'cta');
                return video.status === 'approved' && isMainVideo;
              });

              if (completedVideos.length > 0) {
                processedProjectIds.add(projectId);

                // Get creator info from submission data first
                const creatorId = submissionData.creatorId;
                
                // Always try to fetch from users collection first for accurate data
                let creatorInfo = {
                  name: 'Unknown Creator',
                  handle: '',
                  id: creatorId,
                  avatar: '/creatorimages/creatorpic1v2-min.png'
                };

                if (creatorId) {
                  try {
                    const creatorDoc = await getDoc(doc(db, 'users', creatorId));
                    if (creatorDoc.exists()) {
                      const creatorData = creatorDoc.data();
                      creatorInfo = {
                        name: creatorData.displayName || creatorData.name || submissionData.creatorName || submissionData.creator?.name || 'Unknown Creator',
                        handle: creatorData.handle ? (creatorData.handle.startsWith('@') ? creatorData.handle : `@${creatorData.handle}`) : (submissionData.creatorHandle || submissionData.creator?.handle || ''),
                        id: creatorId,
                        avatar: creatorData.profileImage || creatorData.profileImagePath || creatorData.avatar || creatorData.photoURL || submissionData.creatorAvatar || submissionData.creator?.avatar || submissionData.creator?.profileImage || '/creatorimages/creatorpic1v2-min.png'
                      };
                      console.log(`✓ Fetched creator info for ${creatorId}:`, creatorInfo);
                    } else {
                      // Fallback to submission data if creator doc doesn't exist
                      creatorInfo = {
                        name: submissionData.creatorName || submissionData.creator?.name || 'Unknown Creator',
                        handle: submissionData.creatorHandle || submissionData.creator?.handle || '',
                        id: creatorId,
                        avatar: submissionData.creatorAvatar || submissionData.creator?.avatar || submissionData.creator?.profileImage || '/creatorimages/creatorpic1v2-min.png'
                      };
                      console.log(`⚠️ Creator doc not found, using submission data:`, creatorInfo);
                    }
                  } catch (error) {
                    console.error(`Error fetching creator profile for ${creatorId}:`, error);
                    // Fallback to submission data on error
                    creatorInfo = {
                      name: submissionData.creatorName || submissionData.creator?.name || 'Unknown Creator',
                      handle: submissionData.creatorHandle || submissionData.creator?.handle || '',
                      id: creatorId,
                      avatar: submissionData.creatorAvatar || submissionData.creator?.avatar || submissionData.creator?.profileImage || '/creatorimages/creatorpic1v2-min.png'
                    };
                  }
                } else {
                  // No creatorId, use submission data
                  creatorInfo = {
                    name: submissionData.creatorName || submissionData.creator?.name || 'Unknown Creator',
                    handle: submissionData.creatorHandle || submissionData.creator?.handle || '',
                    id: null,
                    avatar: submissionData.creatorAvatar || submissionData.creator?.avatar || submissionData.creator?.profileImage || '/creatorimages/creatorpic1v2-min.png'
                  };
                }

                const approvedAt = submissionData.submittedAt?.toDate ? 
                  submissionData.submittedAt.toDate() : 
                  (submissionData.submittedAt instanceof Date ? 
                    submissionData.submittedAt : 
                    new Date(submissionData.approvedAt || submissionData.createdAt || new Date()));

                console.log(`Adding completed item:`, {
                  name: creatorInfo.name,
                  handle: creatorInfo.handle,
                  avatar: creatorInfo.avatar,
                  creatorId: creatorId
                });

                completedList.push({
                  id: creatorId || submissionDoc.id,
                  name: creatorInfo.name,
                  handle: creatorInfo.handle,
                  avatar: creatorInfo.avatar,
                  briefId: brief.id,
                  briefName: brief.title || brief.name,
                  projectId: projectId,
                  submissionId: submissionDoc.id,
                  approvedAt: approvedAt,
                  submission: submissionData
                });
              }
            }
          } catch (error) {
            console.error(`Error fetching submissions for brief ${brief.id}:`, error);
          }
        }

        // Sort by approvedAt date (most recent first)
        completedList.sort((a, b) => new Date(b.approvedAt) - new Date(a.approvedAt));

        setRecentlyCompleted(completedList);
      } catch (error) {
        console.error('Error fetching recently completed:', error);
        setRecentlyCompleted([]);
      } finally {
        setLoadingCompleted(false);
      }
    };

    fetchRecentlyCompleted();
  }, [isOpen, script, campaignId, campaignBriefs, onOpenApprovedModal]);

  if (!isOpen || !script) return null;

  // Calculate active creators assigned to this script
  const getActiveCreatorsForScript = () => {
    if (!script.id || !campaignBriefs || !campaignId) return [];
    
    const scriptIdToMatch = String(script.id);
    const activeCreators = [];
    const seenCreatorIds = new Set();
    
    // Get all briefs for this campaign
    const campaignBriefsForScript = campaignBriefs.filter(b => b.campaignId === campaignId);
    
    campaignBriefsForScript.forEach(brief => {
      if (!brief.creators || !Array.isArray(brief.creators)) return;
      
      brief.creators.forEach(creator => {
        const status = creator.status;
        // Include all creators except rejected and completed
        if (status && 
            status !== 'rejected' &&
            status !== 'completed' &&
            !seenCreatorIds.has(creator.id)) {
          
          // Check if creator is assigned to this script
          const creatorScriptIds = creator.scriptIds || [];
          const isAssignedToScript = creatorScriptIds.some(id => String(id) === scriptIdToMatch);
          
          if (isAssignedToScript) {
            seenCreatorIds.add(creator.id);
            activeCreators.push({
              ...creator,
              briefId: brief.id,
              briefName: brief.title || brief.name
            });
          }
        }
      });
    });
    
    return activeCreators;
  };

  const activeCreators = getActiveCreatorsForScript();

  // Debug script data
  console.log('=== SCRIPT DETAIL MODAL ===');
  console.log('Script:', script);
  console.log('Script name:', script.name);
  console.log('Script title:', script.title);
  console.log('Script description:', script.description);
  console.log('Recently completed:', recentlyCompleted);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleteScript) return;
    
    setIsDeleting(true);
    try {
      await onDeleteScript(script);
      setShowDeleteConfirm(false);
      // Small delay to ensure Firestore update completes before closing
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      console.error('Error deleting script:', error);
      alert(`Failed to delete script: ${error.message || 'Unknown error'}`);
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <div className="script-detail-modal-overlay" onClick={onClose}>
      <div className="script-detail-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="script-detail-modal-header">
          <div className="script-detail-modal-title-section">
            <div className="script-detail-modal-title-row">
              <h3 className="script-detail-modal-title">{script.name || script.title || script.scriptName || script.scriptTitle || 'Untitled Script'}</h3>
              {script.isDraft && (
                <span className="script-detail-draft-badge">Draft</span>
              )}
              {!script.isDraft && script.approvalStatus !== 'approved' && script.approvalStatus !== 'rejected' && (script.approvalStatus === 'pending_brand_approval' || script.addedBy === 'admin') && (
                <span className="script-detail-pending-badge">Pending your approval</span>
              )}
              {script.approvalStatus === 'rejected' && (
                <span className="script-detail-rejected-badge">Rejected</span>
              )}
            </div>
            <p className="script-detail-modal-description">{script.description}</p>
          </div>
          <button className="script-detail-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="script-detail-modal-content">
          {/* Script Settings Section */}
          <div className="script-detail-section">
            <div className="script-detail-section-header">
              <h4 className="script-detail-section-title">Script Settings</h4>
            </div>
            <div className="script-detail-settings-grid">
              <div className="script-detail-setting-item">
                <div className="script-detail-setting-label">Platform</div>
                <div className="script-detail-setting-value">
                  <img 
                    src={`/social icons/${(script.settings?.platform || 'tiktok').toLowerCase()}.svg`} 
                    alt={script.settings?.platform || 'TikTok'} 
                    style={{ width: '16px', height: '16px', marginRight: '6px' }}
                  />
                  {script.settings?.platform || 'TikTok'}
                </div>
              </div>
              <div className="script-detail-setting-item">
                <div className="script-detail-setting-label">Video Length</div>
                <div className="script-detail-setting-value">
                  <span className="setting-icon">⏱️</span>
                  {script.settings?.videoLength || 30}s
                </div>
              </div>
              <div className="script-detail-setting-item">
                <div className="script-detail-setting-label">Creative Control</div>
                <div className="script-detail-setting-value">
                  <span className="setting-icon">🎨</span>
                  {(() => {
                    const creativeControl = script.settings?.creativeControl || 'exact';
                    if (creativeControl === 'exact') return 'Scripted';
                    if (creativeControl === 'some' || creativeControl === 'full') return 'Creative';
                    return 'Scripted';
                  })()}
                </div>
              </div>
              {(() => {
                const additionalHooks = script.settings?.additionalHooks || 0;
                const additionalCTAs = script.settings?.additionalCTAs || 0;
                return (
                  <>
                    {additionalHooks > 0 && (
                      <div className="script-detail-setting-item">
                        <div className="script-detail-setting-label">Additional Hooks</div>
                        <div className="script-detail-setting-value">
                          <span className="setting-icon">✨</span>
                          +{additionalHooks}
                        </div>
                      </div>
                    )}
                    {additionalCTAs > 0 && (
                      <div className="script-detail-setting-item">
                        <div className="script-detail-setting-label">Additional CTAs</div>
                        <div className="script-detail-setting-value">
                          <span className="setting-icon">🎯</span>
                          +{additionalCTAs}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Creators Section */}
          <div className="script-detail-section">
            <div className="script-detail-section-header">
              <h4 className="script-detail-section-title">Creators</h4>
              <span className="script-detail-section-count">{activeCreators.length} creators</span>
              {!script.isDraft && (
                <button className="script-modal-add-creator-button" onClick={onAddCreator}>
                  <Plus size={14} />
                  Add Creator
                </button>
              )}
            </div>
            <div className="script-detail-creators-list">
              {activeCreators.length > 0 ? (
                activeCreators.map((creator, index) => {
                  const isPro = creator.tier === 'Pro';
                  const creatorClass = isPro ? 'script-detail-creator-item-pro' : 'script-detail-creator-item-rising';
                  return (
                  <div key={creator.id || creator.projectId || index} className={`script-detail-creator-item ${creatorClass}`}>
                    <div className="script-detail-creator-avatar">
                      {creator.avatar || creator.profileImage ? (
                        <img 
                          src={creator.avatar || creator.profileImage} 
                          alt={creator.name || creator.handle || `Creator ${index + 1}`}
                          onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }}
                        />
                      ) : (
                        <div className="script-detail-creator-avatar-placeholder">
                          <Users size={12} />
                        </div>
                      )}
                    </div>
                    <div className="script-detail-creator-info">
                      <div className="script-detail-creator-name">
                        {creator.handle 
                          ? (creator.handle.startsWith('@') ? creator.handle : `@${creator.handle}`)
                          : creator.name || `Creator ${index + 1}`}
                      </div>
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="script-detail-empty-state">
                  <Users size={24} />
                  <p>No creators assigned</p>
                </div>
              )}
            </div>
          </div>

          {/* Recently Completed Section */}
          <div className="script-detail-section">
            <div className="script-detail-section-header">
              <h4 className="script-detail-section-title">Recently Completed</h4>
              <span className="script-detail-section-count">{(recentlyCompleted || []).length} creator{(recentlyCompleted || []).length !== 1 ? 's' : ''}</span>
            </div>
            {loadingCompleted ? (
              <div className="script-detail-empty-state">
                <Play size={24} />
                <p>Loading...</p>
              </div>
            ) : (recentlyCompleted || []).length > 0 ? (
              <div className="script-detail-approved-videos-grid">
                {recentlyCompleted.map((item, index) => {
                  const formatDate = (date) => {
                    if (!date) return 'N/A';
                    try {
                      const dateObj = date instanceof Date ? date : new Date(date);
                      return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    } catch {
                      return 'N/A';
                    }
                  };

                  const handleClick = () => {
                    if (!onOpenApprovedModal || !item.submission) return;

                    const submission = {
                      id: item.submissionId,
                      ...item.submission,
                      creator: {
                        name: item.name,
                        handle: item.handle,
                        id: item.id
                      },
                      creatorId: item.id,
                      briefId: item.briefId,
                      campaignId: campaignId,
                      briefName: item.briefName,
                      _originalData: {
                        ...item.submission,
                        briefId: item.briefId,
                        campaignId: campaignId
                      }
                    };

                    onOpenApprovedModal(submission);
                  };

                  return (
                    <div 
                      key={`${item.submissionId}-${item.id || index}`} 
                      className="script-detail-approved-video-card"
                      onClick={handleClick}
                    >
                      <div className="script-detail-approved-video-avatar">
                        {item.avatar ? (
                          <img 
                            src={item.avatar} 
                            alt={item.name || 'Creator'}
                            onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }}
                          />
                        ) : (
                          <div className="script-detail-approved-video-avatar-placeholder">
                            <Users size={16} />
                          </div>
                        )}
                      </div>
                      <div className="script-detail-approved-video-info">
                        <div className="script-detail-approved-video-creator-name">
                          {item.name}
                        </div>
                        <div className="script-detail-approved-video-creator-handle">
                          {item.handle ? (item.handle.startsWith('@') ? item.handle : `@${item.handle}`) : ''}
                        </div>
                      </div>
                      <div className="script-detail-approved-video-date-pill">
                        {formatDate(item.approvedAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="script-detail-empty-state">
                <Play size={24} />
                <p>No completed videos yet</p>
              </div>
            )}
          </div>

          {/* Pending approval actions - for scripts added by admin */}
          {!script.isDraft && script.approvalStatus !== 'approved' && script.approvalStatus !== 'rejected' && (script.approvalStatus === 'pending_brand_approval' || script.addedBy === 'admin') && onApproveScript && onRejectScript && (
            <div className="script-detail-pending-section">
              <div className="script-detail-pending-header">
                <h4 className="script-detail-pending-title">Script added by admin – your action needed</h4>
                <p className="script-detail-pending-desc">Approve to use this script, reject to remove it, or edit to make changes. Use &quot;Give feedback&quot; on the card to send feedback.</p>
              </div>
              <div className="script-detail-pending-actions">
                <button className="script-detail-approve-btn" onClick={() => onApproveScript(script)}>
                  <CheckCircle size={16} />
                  Approve
                </button>
                <button className="script-detail-reject-btn" onClick={() => onRejectScript(script)}>
                  <XCircle size={16} />
                  Reject
                </button>
                <button className="script-detail-edit-btn" onClick={onViewScript}>
                  <Edit3 size={16} />
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="script-detail-modal-footer">
          <button
            className="script-detail-delete-button"
            onClick={handleDeleteClick}
            title="Delete script"
          >
            <Trash2 size={16} />
            Delete Script
          </button>
          <button className="script-modal-edit-button" onClick={onViewScript}>
            <Edit3 size={16} />
            Edit Script
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="script-delete-confirm-overlay">
          <div className="script-delete-confirm-container">
            <div className="script-delete-confirm-header">
              <h3>Delete Script</h3>
            </div>
            <div className="script-delete-confirm-content">
              <p className="script-delete-warning">
                Are you sure you want to delete "{script.name || script.title || 'this script'}"?
              </p>
              <p className="script-delete-note">
                This action cannot be undone. All associated data will be permanently removed.
              </p>
            </div>
            <div className="script-delete-confirm-actions">
              <button
                className="script-delete-cancel-button"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="script-delete-confirm-button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Script'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScriptDetailModal;

